import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { EASE, GoldButton, OutlineButton } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [paymentStatus, setPaymentStatus] = useState("checking");

  useEffect(() => {
    if (!sessionId) {
      setPaymentStatus("unknown");
      return;
    }
    let alive = true;
    let tries = 0;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/payments/status/${sessionId}`);
        const d = await res.json();
        if (alive) setPaymentStatus(d.payment_status || d.status || "pending");
      } catch (e) {
        if (alive) setPaymentStatus("pending");
      }
    };
    poll();
    const t = setInterval(() => {
      tries += 1;
      if (tries > 16) {
        clearInterval(t);
        return;
      }
      poll();
    }, 2500);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [sessionId]);

  const confirmed = paymentStatus === "paid";

  return (
    <section className="flex min-h-[100svh] items-center justify-center bg-navy-800 px-5 pt-20">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: EASE }}
        className="max-w-xl text-center"
        data-testid="payment-success-panel"
      >
        {confirmed ? (
          <>
            <CheckCircle2 className="mx-auto h-16 w-16 text-gold" />
            <h1 className="mt-8 font-serif text-5xl font-medium text-ivory sm:text-6xl">
              Booking <em className="italic text-gold">confirmed</em>
            </h1>
            <p className="mt-6 text-sm font-light leading-relaxed text-mist sm:text-base">
              Your adventure is booked! A confirmation is on its way to your inbox, and our team
              will be in touch shortly with everything you need for the day. See you at Rosmoney
              Pier.
            </p>
            <div className="mt-10 flex flex-col justify-center gap-4 sm:flex-row">
              <GoldButton href="/" testId="payment-success-home">Back to Home</GoldButton>
              <OutlineButton href="/experiences" testId="payment-success-experiences">Explore Experiences</OutlineButton>
            </div>
          </>
        ) : (
          <>
            <motion.span
              className="mx-auto block h-14 w-14 rounded-full border-2 border-gold/30 border-t-gold"
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
            />
            <h1 className="mt-8 font-serif text-4xl font-medium text-ivory">
              {paymentStatus === "failed" ? "Payment could not be completed" : "Confirming your payment..."}
            </h1>
            <p className="mt-6 text-sm font-light leading-relaxed text-mist sm:text-base">
              {paymentStatus === "failed"
                ? "The payment didn't go through — you haven't been charged. Please try again or call us on +353 86 251 8252."
                : "Hang tight while we confirm your booking with Stripe. This usually only takes a moment."}
            </p>
            {paymentStatus === "failed" && (
              <div className="mt-10 flex justify-center">
                <GoldButton href="/experiences" testId="payment-retry">Try Again</GoldButton>
              </div>
            )}
          </>
        )}
      </motion.div>
    </section>
  );
}
